import { isEmpty, pick } from 'ramda';
import { createAction } from 'redux-actions';

import { identifyLoggedUser, logger } from '#src/lib/logger';
import { SESSION_STORAGE_KEY } from '#src/lib/session-storage-keys';
import { zE } from '#src/lib/zendesk';
import { isServer } from '#src/lib/utils.mjs';
import { events, trackEvent } from '#src/lib/analytics';

import { identify } from './identify.js';

export const SESSION_GEO_LOCATION = 'SESSION_GEO_LOCATION';
export const SESSION_HYDRATE = 'SESSION_HYDRATE';
export const SESSION_LOGOUT = 'SESSION_LOGOUT';
export const SESSION_REFRESH = 'SESSION_REFRESH';
export const SESSION_REMOVAL = 'SESSION_REMOVAL';
export const SESSION_REMOVAL_PROSPECT_USER = 'SESSION_REMOVAL_PROSPECT_USER';
export const SESSION_SET_AUTH_TOKEN = 'SESSION_SET_AUTH_TOKEN';
export const SESSION_SET_IS_QA_TEAM = 'SESSION_SET_IS_QA_TEAM';
export const SESSION_SET_MEMBERSHIP_RETURN_GAME =
  'SESSION_SET_MEMBERSHIP_RETURN_GAME';
export const SESSION_SET_PROSPECT_USER = 'SESSION_SET_PROSPECT_USER';
export const SESSION_SET_SUPERCHARGER_FIRST_NAME =
  'SESSION_SET_SUPERCHARGER_FIRST_NAME';
export const SESSION_SET_SUPERCHARGER_INVITE_TOKEN =
  'SESSION_SET_SUPERCHARGER_INVITE_TOKEN';
export const SESSION_SET_SUPERCHARGER_PHONE_NUMBER =
  'SESSION_SET_SUPERCHARGER_PHONE_NUMBER';
export const SESSION_SET_SUPERCHARGER_PHONE_COUNTRY =
  'SESSION_SET_SUPERCHARGER_PHONE_COUNTRY';
export const SESSION_SET_SUPERCHARGER_PROMO = 'SESSION_SET_SUPERCHARGER_PROMO';
export const SESSION_SET_USER = 'SESSION_SET_USER';

const unregisterUserLogging = (resetAnalytics = false) => {
  !isServer && resetAnalytics && analytics.reset();
  zE('webWidget', 'reset');
};

const registerUserLogging = (user) => {
  if (isServer || !user?.id) return {};
  const name = user.displayName;
  try {
    identify(user);
    identifyLoggedUser(user.id); // Sentry
    zE('webWidget', 'identify', {
      name,
      email: user.email,
    });
    zE('webWidget', 'prefill', {
      name: {
        value: name,
        readOnly: true,
      },
      email: {
        value: user.email,
      },
    });
    // log help widget events to segment
    zE('webWidget:on', 'userEvent', (event) => {
      trackEvent(
        [events.HELP_WIDGET, event?.action].filter(Boolean).join(' - '),
        event
      );
    });
  } catch (error) {
    logger.info(error, {
      message:
        'Unable to initialize tracking user data. Are you on the server?',
    });
  }
};

export const sessionRemoval = createAction(SESSION_REMOVAL, () => {
  unregisterUserLogging();
});

export const setAuthToken = createAction(SESSION_SET_AUTH_TOKEN);

export const setIsQaTeam = createAction(SESSION_SET_IS_QA_TEAM);

// The pages a Supercharger join starts from: a game's page (with an
// optional product prefix, e.g. /dietbet/games/:gameId) and the GLP Won
// invite landing page. Not the /glp-won/:gameId/... steps after them.
const QA_TEAM_ENTRY_PAGES = [
  /^(\/[^/]+)?\/games\/[^/]+\/?$/,
  /^\/invite-glp-won\/[^/]+\/?$/,
];

// Keeps ?is_qa_team=true from the current URL, on any page, for the rest of
// the session - e.g. a game's page (/games/:gameId?is_qa_team=true, as the
// mobile app opens it on staging), whose Join steps' own URLs don't carry
// it. ?is_qa_team=false on any page turns it off, and so does opening one
// of the entry pages without it; any other page without it changes nothing,
// so the steps keep it.
export const rememberIsQaTeam = (dispatch, query = {}, pathname = '') => {
  if (query?.is_qa_team === 'true') {
    dispatch(setIsQaTeam(true));
  } else if (
    query?.is_qa_team === 'false' ||
    QA_TEAM_ENTRY_PAGES.some((page) => page.test(pathname))
  ) {
    dispatch(setIsQaTeam(false));
  }
};

export const setMembershipReturnGame = createAction(
  SESSION_SET_MEMBERSHIP_RETURN_GAME
);

export const setSuperchargerFirstName = createAction(
  SESSION_SET_SUPERCHARGER_FIRST_NAME
);

// Set once, on first landing on a GLP Won invite page (see
// lib/routes/statics/invite-landing.js) - the rest of the signup funnel is
// only ever reachable from there, but doesn't otherwise carry the invite
// token forward itself (only gameId does). Read back by
// useSuperchargerJoinabilityGuard so a player who becomes unjoinable
// partway through (e.g. the round closed) can be sent back to the same
// invite page rather than the generic game directory.
export const setSuperchargerInviteToken = createAction(
  SESSION_SET_SUPERCHARGER_INVITE_TOKEN
);

export const setSuperchargerPhoneNumber = createAction(
  SESSION_SET_SUPERCHARGER_PHONE_NUMBER
);

export const setSuperchargerPhoneCountry = createAction(
  SESSION_SET_SUPERCHARGER_PHONE_COUNTRY
);

// The pay step's promo code field (see Supercharger/PaymentHostedFields):
// { show, code } - whether to show it, and a code to apply by itself. Both
// arrive on the link that opens the funnel (?use_promo_code=true,
// ?code=CODE), and every later step builds its next URL from
// scratch, dropping the query string - so they're kept for the session,
// the way is_qa_team is (see rememberSuperchargerPromo).
export const setSuperchargerPromo = createAction(
  SESSION_SET_SUPERCHARGER_PROMO
);

// Keeps ?use_promo_code=true / ?code=CODE from the current URL for
// the rest of the session. A page without them changes nothing, so a later
// step never clears what the link brought.
export const rememberSuperchargerPromo = (dispatch, query = {}) => {
  const code = typeof query.code === 'string' ? query.code.trim() : '';
  const show = query.use_promo_code === 'true' || !!code;
  if (!show) return;

  dispatch(setSuperchargerPromo({ show: true, code: code || null }));
};

export const hydrateSessionState = createAction(SESSION_HYDRATE, () => {
  const rawSession = window.sessionStorage.getItem(SESSION_STORAGE_KEY);

  try {
    const session = JSON.parse(rawSession);
    return session;
  } catch {
    return {};
  }
});

export const clearProspectUser = createAction(
  SESSION_REMOVAL_PROSPECT_USER,
  () => {
    // do not clear analytics when transitioning between prospect and real user,
    // regardless of whether it's ultimately the same user id or not, otherwise
    // the anon id will change, making analytics inconsistent.
    unregisterUserLogging(false);
  }
);

const selectSessionUserAttributes = pick([
  'id',
  'email',
  'firstName',
  'lastName',
  'displayName',
  'role',
]);

export const setProspectUser = createAction(
  SESSION_SET_PROSPECT_USER,
  (user) => {
    if (!user?.id) return {};
    registerUserLogging(user);
    return selectSessionUserAttributes(user);
  }
);

/**
 * Set the authenticated user.
 */
export const setSessionUser = createAction(SESSION_SET_USER, (user) => {
  if (!user?.id) return {};
  const fallbackName = `${user.firstName || ''} ${user.lastName || ''}`?.trim();
  const name = user.displayName || fallbackName;

  registerUserLogging(user);

  let sessionUser = selectSessionUserAttributes(user);
  return isEmpty(name) ? sessionUser : { ...sessionUser, name };
});

/**
 * Set user geo location
 */
export const setGeoLocation = createAction(
  SESSION_GEO_LOCATION,
  (geoLocation) => geoLocation
);
